import { Suspense, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazyWithReload } from '@/lib/chunkReload';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@/auth/AuthProvider';
import { RequireAuth } from '@/auth/RequireAuth';
import { IsOwnerGate } from '@/auth/IsOwnerGate';
import { ThemeProvider } from '@/theme/ThemeProvider';
import { LocaleProvider } from '@/i18n/LocaleProvider';
import { ToastProvider } from '@/components/toast/ToastProvider';
import { PwaUpdatePrompt } from '@/pwa/PwaUpdatePrompt';
import { LiveSessionProvider } from '@/state/LiveSessionContext';
import { CommandPaletteProvider } from '@/components/CommandPalette';
import { MainLayout } from '@/components/MainLayout';
import { DesktopLayout } from '@/components/DesktopLayout';
import { useViewport } from '@/hooks/useViewport';
import { useOnlineGuard } from '@/hooks/useOnlineGuard';
import { useAppBadge } from '@/hooks/useAppBadge';
import { usePeriodicSync } from '@/hooks/usePeriodicSync';

const Welcome = lazyWithReload(() => import('@/pages/Welcome').then(m => ({ default: m.Welcome })));
const Suspended = lazyWithReload(() => import('@/pages/Suspended').then(m => ({ default: m.Suspended })));

/* Terms/Privacy now live on the marketing site (optimizehealth.ca). These
   stub redirects preserve old in-app and external bookmarks to /terms and
   /privacy. */
function LegalRedirect({ to }: { to: string }) {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}
const Today = lazyWithReload(() => import('@/pages/Today').then(m => ({ default: m.Today })));
const Settings = lazyWithReload(() => import('@/pages/Settings').then(m => ({ default: m.Settings })));
const Body = lazyWithReload(() => import('@/pages/Body').then(m => ({ default: m.Body })));
const Train = lazyWithReload(() => import('@/pages/Train').then(m => ({ default: m.Train })));
const LiveSession = lazyWithReload(() => import('@/pages/LiveSession').then(m => ({ default: m.LiveSession })));
const Food = lazyWithReload(() => import('@/pages/Food').then(m => ({ default: m.Food })));
const Meds = lazyWithReload(() => import('@/pages/Meds').then(m => ({ default: m.Meds })));
const MedsCalculator = lazyWithReload(() => import('@/pages/MedsCalculator').then(m => ({ default: m.MedsCalculator })));
const Plan = lazyWithReload(() => import('@/pages/Plan').then(m => ({ default: m.Plan })));
const PlanBuilder = lazyWithReload(() => import('@/pages/PlanBuilder').then(m => ({ default: m.PlanBuilder })));
const Onboarding = lazyWithReload(() => import('@/pages/Onboarding').then(m => ({ default: m.Onboarding })));
const Feedback = lazyWithReload(() => import('@/pages/Feedback').then(m => ({ default: m.Feedback })));
const AdminLayout = lazyWithReload(() => import('@/components/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const AdminOverviewPage = lazyWithReload(() => import('@/pages/admin/AdminOverviewPage').then(m => ({ default: m.AdminOverviewPage })));
const AdminGrowthPage = lazyWithReload(() => import('@/pages/admin/AdminGrowthPage').then(m => ({ default: m.AdminGrowthPage })));
const AdminAdoptionPage = lazyWithReload(() => import('@/pages/admin/AdminAdoptionPage').then(m => ({ default: m.AdminAdoptionPage })));
const AdminAiUsagePage = lazyWithReload(() => import('@/pages/admin/AdminAiUsagePage').then(m => ({ default: m.AdminAiUsagePage })));
const AdminAccountsPage = lazyWithReload(() => import('@/pages/admin/AdminAccountsPage').then(m => ({ default: m.AdminAccountsPage })));
const AdminRequestsPage = lazyWithReload(() => import('@/pages/admin/AdminRequestsPage').then(m => ({ default: m.AdminRequestsPage })));
const AdminFlagsPage = lazyWithReload(() => import('@/pages/admin/AdminFlagsPage').then(m => ({ default: m.AdminFlagsPage })));
const AdminUserDetailPage = lazyWithReload(() => import('@/pages/admin/AdminUserDetailPage').then(m => ({ default: m.AdminUserDetailPage })));
const AdminMonitoringPage = lazyWithReload(() => import('@/pages/admin/AdminMonitoringPage').then(m => ({ default: m.AdminMonitoringPage })));
const NotFound = lazyWithReload(() => import('@/pages/NotFound').then(m => ({ default: m.NotFound })));
const Offline = lazyWithReload(() => import('@/pages/Offline').then(m => ({ default: m.Offline })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

function ResponsiveLayout({ children }: { children: React.ReactNode }) {
  const vp = useViewport();
  if (vp === 'desktop') return <DesktopLayout>{children}</DesktopLayout>;
  if (vp === 'tablet') return <DesktopLayout compact>{children}</DesktopLayout>;
  return <MainLayout>{children}</MainLayout>;
}

function Authed({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <ResponsiveLayout>{children}</ResponsiveLayout>
    </RequireAuth>
  );
}

// Routes that should render full-bleed without the TabBar — mirror the
// Blazor pages that used EmptyLayout (PlanBuilder, Onboarding).
function AuthedFull({ children }: { children: React.ReactNode }) {
  return <RequireAuth>{children}</RequireAuth>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
        <ThemeProvider>
          <AuthProvider>
            <ToastProvider>
              <PwaUpdatePrompt />
              <LiveSessionProvider>
                <BrowserRouter>
                  <RouterChildren />
                </BrowserRouter>
              </LiveSessionProvider>
            </ToastProvider>
          </AuthProvider>
        </ThemeProvider>
      </LocaleProvider>
    </QueryClientProvider>
  );
}

function RouterChildren() {
  useOnlineGuard();
  useAppBadge();
  usePeriodicSync();
  return (
    <CommandPaletteProvider>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/suspended" element={<Suspended />} />
          <Route path="/terms" element={<LegalRedirect to="https://optimizehealth.ca/terms.html" />} />
          <Route path="/privacy" element={<LegalRedirect to="https://optimizehealth.ca/privacy.html" />} />
          <Route path="/offline" element={<MainLayout><Offline /></MainLayout>} />
          <Route path="/" element={<Authed><Today /></Authed>} />
          <Route path="/settings" element={<Authed><Settings /></Authed>} />
          <Route path="/food" element={<Authed><Food /></Authed>} />
          <Route path="/body" element={<Authed><Body /></Authed>} />
          <Route path="/train" element={<Authed><Train /></Authed>} />
          <Route path="/train/session" element={<AuthedFull><LiveSession /></AuthedFull>} />
          <Route path="/meds" element={<Authed><Meds /></Authed>} />
          <Route path="/meds/calculator" element={<Authed><MedsCalculator /></Authed>} />
          <Route path="/plan" element={<Authed><Plan /></Authed>} />
          <Route path="/plan/new" element={<AuthedFull><PlanBuilder /></AuthedFull>} />
          <Route path="/plan/edit" element={<AuthedFull><PlanBuilder /></AuthedFull>} />
          <Route path="/setup" element={<Authed><Onboarding /></Authed>} />
          <Route path="/feedback" element={<Authed><Feedback /></Authed>} />
          {/* Owner-only admin area — invisible to non-owners (no TabBar/nav entry).
              RequireAuth handles anonymous users; IsOwnerGate redirects non-owners. */}
          <Route element={<RequireAuth><IsOwnerGate /></RequireAuth>}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminOverviewPage />} />
              <Route path="growth" element={<AdminGrowthPage />} />
              <Route path="adoption" element={<AdminAdoptionPage />} />
              <Route path="ai" element={<AdminAiUsagePage />} />
              <Route path="requests" element={<AdminRequestsPage />} />
              <Route path="requests/:id" element={<AdminRequestsPage />} />
              <Route path="accounts" element={<AdminAccountsPage />} />
              <Route path="users/:email" element={<AdminUserDetailPage />} />
              <Route path="flags" element={<AdminFlagsPage />} />
              <Route path="monitoring" element={<AdminMonitoringPage />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </CommandPaletteProvider>
  );
}
